import {useParams, useNavigate} from "react-router-dom";
import {useEffect, useState} from "react";
import api from "../api";
import {Col, Progress, Row, Spin, Typography, Card, Button, Statistic, Divider} from "antd";
import {useForm} from "antd/es/form/Form";
import {useDispatch, useSelector} from "react-redux";
import QuestionItem from "./Fragments/QuestionItem";
import {handleQuizStat} from "../functions";
import ReactGA from "react-ga";
import {useTranslation} from "react-i18next";
import {CheckCircleOutlined, CloseCircleOutlined, ArrowLeftOutlined, ReloadOutlined, UnorderedListOutlined} from '@ant-design/icons';
import {Helmet} from "react-helmet-async";
import Comments from "./Fragments/Comments";
import DynamicAdBanner from "../DynamicAddBanner";
import ChatPromoBanner from "../Fragments/ChatPromoBanner";

const {Title, Text} = Typography;

function Quiz({analytics}) {
    const {id} = useParams()
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [formRef] = useForm();
    const [quiz, setQuiz] = useState(null)
    const [step, setStep] = useState(0);
    const [points, setPoints] = useState(0);
    const [answers, setAnswers] = useState({});
    const [wrongAnswers, setWrongAnswers] = useState([]);
    const [loading, setLoading] = useState(true)
    const [showReview, setShowReview] = useState(false);
    const dispatch = useDispatch();
    const {settings} = useSelector(state=>state)


    useEffect(() => {
        analytics()
        api.API.get(api.Quize + `/${id}`).then(data => {
            setQuiz(data)
            setLoading(false)
        })
    }, [id])

    let currentQuestion = quiz ? quiz.questions[step] : null
    
    const handleNext = (variant) => {
        formRef.resetFields()
        let safe_points = points;
        handleQuizStat(currentQuestion.id, variant === currentQuestion.answer)
        setAnswers({
            ...answers,
            [step]: variant
        })

        if (variant === currentQuestion.answer) {
            safe_points++
            setPoints(safe_points)
        } else {
            setWrongAnswers([...wrongAnswers, currentQuestion.id])
        }
        
        if(step === 19){
            ReactGA.event({
                category: 'Quiz',
                action: `passed-${id}-${safe_points}`,
            });
            dispatch({
                payload:{[id]:safe_points},
                type:'QUIZ_PASSED'
            })
            dispatch({
                payload:wrongAnswers,
                type:'QUESTIONS_PASSED'
            })
        }

        setStep(step + 1)
    }

    const resetQuiz = () => {
        setStep(0);
        setPoints(0);
        setAnswers({});
        setWrongAnswers([]);
        setShowReview(false);
    }

    if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>

    const isPassed = points >= 18;

    return (
        <div className="quiz-container" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Helmet>
                <title>{t('seo.quiz_title', { number: quiz?.number })}</title>
                <meta name="description" content={t('seo.quiz_desc', { number: quiz?.number })} />
                <meta property="og:title" content={t('seo.quiz_title', { number: quiz?.number })} />
                <meta property="og:description" content={t('seo.quiz_desc', { number: quiz?.number })} />
            </Helmet>

            <Row gutter={[16, 16]}>
                {step < 20 && (
                    <Col span={24}>
                        <Card size="small" style={{ marginBottom: 20, textAlign: 'center', borderRadius: 8 }}>
                            <Title level={4} style={{ margin: 0 }}>{t('questions.tests')} №{quiz?.number}</Title>
                            <Text type="secondary">{step + 1} / 20</Text>
                            <Progress 
                                percent={Math.round((step / 20) * 100)} 
                                strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                                status="active"
                                showInfo={false}
                            />
                        </Card>
                    </Col>
                )}

                <Col span={24}>
                    {step === 20 ? (
                        <div className="results-dashboard">
                            <Card
                                style={{
                                    textAlign: 'center',
                                    borderRadius: 12,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    borderTop: `6px solid ${isPassed ? '#52c41a' : '#ff4d4f'}`
                                }}
                            >
                                <Title
                                    level={2}>{isPassed ? t('questions.congrats') : t('questions.failed_desc')}</Title>

                                <Row justify="center" align="middle" gutter={[16, 16]}>
                                    <Col xs={24} sm={12} md={12}>
                                        <Progress
                                            type="circle"
                                            percent={(points / 20) * 100}
                                            format={() => `${points}/20`}
                                            width={140}
                                            strokeColor={isPassed ? '#52c41a' : '#ff4d4f'}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={12}>
                                        <Row gutter={[8, 8]}>
                                            <Col span={12} sm={24}>
                                                <Statistic
                                                    title={t('home.true_answers')}
                                                    value={points}
                                                    valueStyle={{color: '#3f8600', fontSize: '20px'}}
                                                    prefix={<CheckCircleOutlined/>}
                                                />
                                            </Col>
                                            <Col span={12} sm={24}>
                                                <Statistic
                                                    title={t('home.wrong_answers')}
                                                    value={20 - points}
                                                    valueStyle={{color: '#cf1322', fontSize: '20px'}}
                                                    prefix={<CloseCircleOutlined/>}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Divider/>

                                <Row gutter={[8, 8]} justify="center">
                                    <Col xs={24} sm={8}>
                                        <Button
                                            icon={<ReloadOutlined/>}
                                            block
                                            onClick={resetQuiz}
                                        >
                                            {t('questions.try_again')}
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Button
                                            type="primary"
                                            icon={<UnorderedListOutlined/>}
                                            block
                                            onClick={() => setShowReview(!showReview)}
                                        >
                                            {t('questions.review_answers')}
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Button
                                            icon={<ArrowLeftOutlined/>}
                                            block
                                            onClick={() => navigate('/quizes')}
                                        >
                                            {t('questions.back_to_list')}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            {showReview && (
                                <div style={{marginTop: 30}}>
                                    <Title level={3}
                                           style={{textAlign: 'center'}}>{t('questions.review_answers')}</Title>
                                    {quiz.questions.map((question, key) => (
                                        <Card key={key} style={{marginBottom: 15, borderRadius: 8}}>
                                            <QuestionItem
                                                dispatch={dispatch}
                                                value={answers[key]}
                                                currentQuestion={question}
                                                noSubmit={true}
                                            />
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        currentQuestion && (
                            <QuestionItem
                                dispatch={dispatch}
                                quick_view={settings.quick_view}
                                formRef={formRef}
                                handleNext={handleNext}
                                currentQuestion={currentQuestion}
                            />
                        )
                    )}
                </Col>
            </Row>
        {/*    <DynamicAdBanner bannerId="6112607" />*/}
            <Comments quizId={id}/>
        </div>
    )
}

export default Quiz;
